import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Package, User as UserIcon, MapPin, Clock, CreditCard, Activity, ChevronRight } from 'lucide-react';
import { adminApi, resolveUrl } from '@/services/adminApi';
import type { AdminOrderDetail, AdminOrderItem, AdminPaymentEvent } from '@/services/adminApi';
import { queryKeys } from '@/lib/queryKeys';
import { ORDER_STATUS_LABELS, PAYMENT_STATUS_LABELS, ORDER_PROGRESS_STAGES, CANCELLABLE_STATUSES, type OrderStatus, type PaymentStatus } from '@printtat/shared';
import { formatPrice } from '@printtat/shared';
import { PaymentMethodLabel, PageHeader, PageContent, Button, IconButton, LoadingState, Table, AlertDialog, TextField, Select, StatusBadge } from '@printtat/ui';
import type { Column } from '@printtat/ui';

const ItemPreview = ({ src }: { src: string }) => {
  const [failed, setFailed] = useState(false);
  if (failed) return <div className="w-10 h-10 rounded bg-gray-100" />;
  return <img src={src} alt="" crossOrigin="anonymous" className="w-10 h-10 rounded object-cover bg-gray-100" onError={() => setFailed(true)} />;
};

const ORDER_STATUS_COLORS: Record<string, 'yellow' | 'blue' | 'indigo' | 'violet' | 'purple' | 'cyan' | 'green' | 'red' | 'orange' | 'gray'> = {
  pending: 'yellow', confirmed: 'blue', in_production: 'indigo', quality_check: 'violet',
  shipped: 'purple', out_for_delivery: 'cyan', delivered: 'green', cancelled: 'red', returned: 'orange',
};

const STATUS_BAR_COLORS: Record<string, string> = {
  pending: 'bg-yellow-50 border-yellow-200',
  confirmed: 'bg-blue-50 border-blue-200',
  in_production: 'bg-indigo-50 border-indigo-200',
  quality_check: 'bg-violet-50 border-violet-200',
  shipped: 'bg-purple-50 border-purple-200',
  out_for_delivery: 'bg-cyan-50 border-cyan-200',
  delivered: 'bg-green-50 border-green-200',
  cancelled: 'bg-red-50 border-red-200',
  returned: 'bg-orange-50 border-orange-200',
};

const PAYMENT_STATUS_COLORS: Record<string, 'yellow' | 'amber' | 'green' | 'red' | 'gray' | 'orange'> = {
  pending: 'yellow', awaiting_payment: 'amber', paid: 'green', failed: 'red', refund_pending: 'orange', refunded: 'gray',
};

const SEVERITY_COLORS: Record<string, 'red' | 'amber' | 'gray'> = {
  error: 'red', warn: 'amber', info: 'gray',
};

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const getNextStatus = (current: string): string | null => {
  const idx = ORDER_PROGRESS_STAGES.indexOf(current as OrderStatus);
  if (idx === -1 || idx >= ORDER_PROGRESS_STAGES.length - 1) return null;
  return ORDER_PROGRESS_STAGES[idx + 1];
};

const STATUS_OPTIONS = ORDER_PROGRESS_STAGES.map(s => ({
  value: s,
  label: ORDER_STATUS_LABELS[s],
}));

export const AdminOrderDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [statusNote, setStatusNote] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [cancelConfirm, setCancelConfirm] = useState(false);

  const { data: order, isLoading } = useQuery<AdminOrderDetail>({
    queryKey: queryKeys.orders.detail(id!),
    queryFn: () => adminApi.orders.detail(id!),
    enabled: !!id,
  });

  const { data: paymentEventsData } = useQuery<AdminPaymentEvent[]>({
    queryKey: [...queryKeys.orders.detail(id!), 'payment-events'],
    queryFn: () => adminApi.orders.paymentEvents(id!),
    enabled: !!id && order?.paymentMethod === 'online',
  });

  const updateStatus = useMutation({
    mutationFn: ({ status, note }: { status: string; note?: string }) =>
      adminApi.orders.updateStatus(id!, status, note),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'orders'] });
      setStatusNote('');
      setSelectedStatus('');
      setCancelConfirm(false);
    },
  });

  if (isLoading || !order) {
    return (
      <PageContent>
        <LoadingState variant="spinner" message="Loading order..." />
      </PageContent>
    );
  }

  const nextStatus = getNextStatus(order.status);
  const canCancel = CANCELLABLE_STATUSES.includes(order.status as OrderStatus);
  const isTerminal = order.status === 'delivered' || order.status === 'cancelled' || order.status === 'returned';
  const address = order.shippingAddress as Record<string, string>;

  const effectiveStatus = selectedStatus || nextStatus || '';
  const hasStatusChange = effectiveStatus !== '' && effectiveStatus !== order.status;

  const itemColumns: Column<AdminOrderItem>[] = [
    {
      key: 'preview',
      header: '',
      className: 'w-14',
      render: (item) => (
        item.previewUrl
          ? <ItemPreview src={resolveUrl(item.previewUrl)} />
          : <div className="w-10 h-10 rounded bg-gray-100" />
      ),
    },
    {
      key: 'product',
      header: 'Product',
      render: (item) => (
        <div>
          <p className="text-sm font-medium text-gray-900">{item.designName}</p>
          <p className="text-xs text-gray-500">{item.productName}{item.variantLabel ? ` · ${item.variantLabel}` : ''}</p>
        </div>
      ),
    },
    {
      key: 'quantity',
      header: 'Qty',
      render: (item) => <span className="text-sm text-gray-600">{item.quantity}</span>,
    },
    {
      key: 'unitPrice',
      header: 'Unit Price',
      render: (item) => <span className="text-sm text-gray-600">{formatPrice(item.unitPrice)}</span>,
    },
    {
      key: 'total',
      header: 'Total',
      align: 'right',
      render: (item) => <span className="text-sm font-medium text-gray-900">{formatPrice(item.unitPrice * item.quantity)}</span>,
    },
  ];

  return (
    <PageContent>
      {/* Header */}
      <div className="flex items-start gap-3">
        <IconButton size="sm" onClick={() => navigate('/orders')}>
          <ArrowLeft className="w-4 h-4 text-gray-600" />
        </IconButton>
        <div className="flex-1">
          <PageHeader
            icon={Package}
            title={order.orderNumber}
            description={`Placed ${formatDate(order.createdAt)}`}
          />
        </div>
      </div>

      {/* Status bar — top of page */}
      <div className={`mt-4 border rounded-xl px-4 py-2.5 ${STATUS_BAR_COLORS[order.status] ?? 'bg-gray-50 border-gray-200'}`}>
        {isTerminal ? (
          <div className="flex items-center gap-3">
            <StatusBadge label={ORDER_STATUS_LABELS[order.status as OrderStatus] ?? order.status} color={ORDER_STATUS_COLORS[order.status] ?? 'gray'} size="xs" />
            <span className="text-sm text-gray-500">This order is {order.status}</span>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <StatusBadge label={ORDER_STATUS_LABELS[order.status as OrderStatus] ?? order.status} color={ORDER_STATUS_COLORS[order.status] ?? 'gray'} size="xs" />
            <ChevronRight size={12} className="text-gray-400 shrink-0" />
            <Select
              options={STATUS_OPTIONS}
              value={effectiveStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              size="sm"
            />
            <div className="flex-1 min-w-0">
              <TextField
                className="h-8!"
                value={statusNote}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStatusNote(e.target.value)}
                placeholder="Note (optional)..."
              />
            </div>
            <Button
              size="sm"
              loading={updateStatus.isPending}
              disabled={!hasStatusChange || updateStatus.isPending}
              onClick={() => {
                if (hasStatusChange) updateStatus.mutate({ status: effectiveStatus, note: statusNote || undefined });
              }}
            >
              Update to {ORDER_STATUS_LABELS[effectiveStatus as OrderStatus] ?? effectiveStatus}
            </Button>
            {canCancel && (
              <Button
                size="sm"
                color="red"
                variant="outline"
                onClick={() => setCancelConfirm(true)}
              >
                Cancel Order
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Customer + Shipping + Payment */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Customer & Shipping */}
        <div className="border rounded-xl bg-white p-4 space-y-4">
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
              <UserIcon size={14} className="text-gray-400" /> Customer
            </h3>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-700">{order.customerName}</p>
                {order.customerEmail && <p className="text-xs text-gray-500">{order.customerEmail}</p>}
              </div>
              <button
                onClick={() => navigate(`/users/${order.customerId}`)}
                className="text-xs text-blue-500 hover:text-blue-700"
              >
                View profile
              </button>
            </div>
          </div>
          <div className="border-t pt-4 space-y-2">
            <h3 className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
              <MapPin size={14} className="text-gray-400" /> Shipping Address
            </h3>
            <div className="text-sm text-gray-600 space-y-0.5">
              <p>{address.fullName}</p>
              <p>{address.addressLine1}</p>
              {address.addressLine2 && <p>{address.addressLine2}</p>}
              <p>{address.city}, {address.governorate}</p>
              {address.phone && <p className="text-xs text-gray-400">{address.phone}</p>}
            </div>
          </div>
        </div>

        {/* Payment */}
        <div className="border rounded-xl bg-white p-4 space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
              <CreditCard size={14} className="text-gray-400" /> Payment
            </h3>
            <StatusBadge label={PAYMENT_STATUS_LABELS[order.paymentStatus as PaymentStatus] ?? order.paymentStatus} color={PAYMENT_STATUS_COLORS[order.paymentStatus] ?? 'gray'} size="xs" />
          </div>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between items-center">
              <span className="text-gray-500">Method</span>
              <span className="font-medium text-gray-700">
                <PaymentMethodLabel
                  paymentMethod={order.paymentMethod}
                  paymentMethodDetail={order.paymentDetails?.paymentMethod}
                  cardBrand={order.paymentDetails?.cardBrand}
                  cardLastFour={order.paymentDetails?.cardLastFour}
                  iconSize="w-3.5 h-3.5"
                />
              </span>
            </div>
            {order.paymentDetails?.transactionId && (
              <div className="flex justify-between">
                <span className="text-gray-500">Transaction</span>
                <span className="font-mono text-xs text-gray-700">{order.paymentDetails.transactionId}</span>
              </div>
            )}
            {order.paymentDetails?.paidAt && (
              <div className="flex justify-between">
                <span className="text-gray-500">Paid at</span>
                <span className="text-xs text-gray-700">{formatDate(order.paymentDetails.paidAt)}</span>
              </div>
            )}
            <div className="flex justify-between pt-1 border-t">
              <span className="text-gray-500">Subtotal</span>
              <span>{formatPrice(order.subtotal)}</span>
            </div>
            {order.discountAmount > 0 && (
              <div className="flex justify-between">
                <span className="text-green-600">
                  Discount
                  {order.discountDetails
                    ? ` (${(order.discountDetails as Record<string, unknown>).code})`
                    : ""}
                </span>
                <span className="text-green-600">−{formatPrice(order.discountAmount)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-500">Shipping</span>
              <span>{formatPrice(order.shippingCost)}</span>
            </div>
            <div className="flex justify-between font-medium text-gray-900">
              <span>Total</span>
              <span>{formatPrice(order.totalAmount)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Items */}
      <div className="mt-6">
        <h3 className="text-sm font-medium text-gray-900 mb-3">Items ({order.items.length})</h3>
        <Table
          columns={itemColumns}
          data={order.items}
          keyExtractor={(item) => item.id}
        />
      </div>

      {/* Status History + Payment Events side by side */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Status History */}
        <div>
          <h3 className="text-sm font-medium text-gray-900 flex items-center gap-1.5 mb-3">
            <Clock size={14} className="text-gray-400" /> Status History
          </h3>
          <div className="border rounded-xl bg-white divide-y">
            {[...order.statusHistory].reverse().map((entry, i) => (
              <div key={i} className="px-4 py-3 flex items-start gap-3">
                <StatusBadge label={ORDER_STATUS_LABELS[entry.status as OrderStatus] ?? entry.status} color={ORDER_STATUS_COLORS[entry.status] ?? 'gray'} size="xs" />
                <span className="text-xs text-gray-400 whitespace-nowrap shrink-0">{formatDate(entry.timestamp)}</span>
                {entry.note && <span className="text-xs text-gray-500 min-w-0">— {entry.note}</span>}
                {entry.changedBy && <span className="text-xs text-gray-400 ml-auto whitespace-nowrap">by {entry.changedBy}</span>}
              </div>
            ))}
          </div>
        </div>

        {/* Payment Events */}
        {paymentEventsData && paymentEventsData.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-gray-900 flex items-center gap-1.5 mb-3">
              <Activity size={14} className="text-gray-400" /> Payment Events
            </h3>
            <div className="border rounded-xl bg-white divide-y">
              {paymentEventsData.map((event) => (
                <div key={event.id} className="px-4 py-3 flex items-start gap-3">
                  <span className="shrink-0"><StatusBadge label={event.type.replace(/_/g, ' ')} color={SEVERITY_COLORS[event.severity ?? ''] ?? 'gray'} size="xs" /></span>
                  <span className="text-xs text-gray-400 whitespace-nowrap shrink-0">{formatDate(event.createdAt)}</span>
                  <span className="text-xs text-gray-400 min-w-0 wrap-break-word">
                    {[
                      event.paymobTransactionId ? `txn: ${event.paymobTransactionId}` : '',
                      event.data && Object.keys(event.data).length > 0
                        ? Object.entries(event.data).map(([k, v]) => `${k}: ${v}`).join(', ')
                        : '',
                    ].filter(Boolean).join('  ')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Cancel confirmation */}
      <AlertDialog
        open={cancelConfirm}
        onOpenChange={setCancelConfirm}
        title="Cancel order"
        description={`Cancel order ${order.orderNumber}? This cannot be undone.`}
        confirmText="Cancel order"
        variant="destructive"
        loading={updateStatus.isPending}
        onConfirm={() => updateStatus.mutate({ status: 'cancelled', note: 'Cancelled by admin' })}
        onCancel={() => setCancelConfirm(false)}
      />
    </PageContent>
  );
};
